/**
 * The shared "that did not work" banner (Daniel 2026-10-14). Fed by
 * lib/error-bus.ts. Mounted once in main.tsx, outside the router and the
 * layouts, so it sits above every page and no ancestor can clip it.
 */
import { useState } from 'react';
import { dismissFailure, useFailureNotices, type FailureNotice } from '../lib/error-bus';

function Notice({ n }: { n: FailureNotice }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(n.details);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked; fall back to selecting nothing and leaving the ref on screen.
    }
  }
  return (
    <div
      role="alert"
      style={{
        pointerEvents: 'auto',
        background: 'var(--bg-surface)',
        border: '1px solid var(--danger)',
        borderLeft: '4px solid var(--danger)',
        borderRadius: 10,
        boxShadow: 'var(--shadow-md, 0 6px 20px rgba(0,0,0,0.18))',
        padding: '10px 12px',
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-heading)' }}>
          {n.title}
          {n.count > 1 ? <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}> (x{n.count})</span> : null}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-body)', marginTop: 2, lineHeight: 1.45 }}>{n.message}</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
          {n.ref && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Ref <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-heading)' }}>{n.ref}</strong>
            </span>
          )}
          <button
            type="button"
            onClick={copy}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              font: 'inherit', fontSize: 12, fontWeight: 600, color: 'var(--brand-primary)',
            }}
          >
            {copied ? 'Copied' : 'Copy details'}
          </button>
        </div>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => dismissFailure(n.id)}
        style={{
          background: 'transparent', border: 0, padding: 0, cursor: 'pointer',
          color: 'var(--text-muted)', fontSize: 18, lineHeight: 1, flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}

export default function ErrorBanner() {
  const notices = useFailureNotices();
  if (notices.length === 0) return null;
  return (
    <div
      style={{
        position: 'fixed', left: 0, right: 0, bottom: 16, zIndex: 1000,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        padding: '0 16px', pointerEvents: 'none',
      }}
    >
      <div style={{ width: '100%', maxWidth: 520, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {notices.map((n) => <Notice key={n.id} n={n} />)}
      </div>
    </div>
  );
}
